import type { ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { EmptyState, Spinner } from './ui';
import { ErrorPanel } from './ErrorPanel';
import { SettingsEditor } from './SettingsEditor';

export type SettingsScope = 'sites' | 'groups';

/** What a module's own settings card receives. */
export interface SettingsCardProps {
  scope: SettingsScope;
  /** Site or group id. */
  scopeId: string;
  moduleKey: string;
  canEdit: boolean;
}

const registry = new Map<string, ComponentType<SettingsCardProps>>();

/**
 * A module can draw its own card (a colour picker, a preview) instead of the generic form.
 * Call once at start-up, e.g. `registerSettingsCard('badges', BadgeSettingsCard)`.
 */
export function registerSettingsCard(moduleKey: string, component: ComponentType<SettingsCardProps>): void {
  registry.set(moduleKey, component);
}

const ModuleList = z.array(z.object({ moduleKey: z.string(), nameKey: z.string() }));

/**
 * One card section per module that has settings for this site or group.
 * Uses `GET /{sites|groups}/:id/settings` for the list and `/:moduleKey` for each form.
 * `exclude` leaves out modules the page already shows itself (the sites page draws its own look and privacy cards).
 */
export function SettingsCards({
  scope,
  scopeId,
  canEdit,
  exclude = [],
}: {
  scope: SettingsScope;
  scopeId: string;
  canEdit: boolean;
  exclude?: readonly string[];
}) {
  const base = `/${scope}/${scopeId}/settings`;
  const q = useQuery({ queryKey: ['settings', base, 'modules'], queryFn: () => api.get(base, { schema: ModuleList }) });
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const modules = q.data.filter((m) => !exclude.includes(m.moduleKey));
  if (modules.length === 0) return <EmptyState message={t('settingsCards.empty')} />;
  return (
    <div className="space-y-8">
      {modules.map((m) => {
        const Custom = registry.get(m.moduleKey);
        return (
          <section key={m.moduleKey} aria-labelledby={`sc-${m.moduleKey}`}>
            <h2 id={`sc-${m.moduleKey}`} className="mb-3 text-lg font-semibold text-slate-900">
              {t(m.nameKey)}
            </h2>
            {Custom ? (
              <Custom scope={scope} scopeId={scopeId} moduleKey={m.moduleKey} canEdit={canEdit} />
            ) : (
              <SettingsEditor moduleKey={m.moduleKey} base={base} canEdit={canEdit} />
            )}
          </section>
        );
      })}
    </div>
  );
}
